import { Injectable, signal } from '@angular/core';
import { SedeScope } from './sede-scope.util';

/**
 * Alcance de sedes de la pantalla de Reportes. Lo fija la pagina mientras esta abierta (y lo borra
 * al salir) y el `sedeInterceptor` lo aplica a los requests de reportes: asi no hay que pasar el
 * alcance como argumento por los ~60 metodos de `ReportsService`, y NO puede filtrarse a otra
 * pantalla (Pedidos, Cajas, POS siguen con la sede activa global).
 *
 * `undefined` = sin alcance explicito (camino global de siempre).
 */
@Injectable({ providedIn: 'root' })
export class ReportsSedeScopeHolder {
  readonly scope = signal<SedeScope | undefined>(undefined);
}

const REPORTS_PATH_PREFIX = '/owner/reports/';
const SURVEYS_PATH_PREFIX = '/owner/surveys/';
const SURVEY_KPIS_PATH = '/owner/surveys/kpis';

/**
 * Rutas de reportes que el backend NO deja pedir con varias sedes (no estan en
 * `MultiSedeRoutes`): responderian 400 `MULTI_SEDE_NOT_SUPPORTED`. Son una lista de EXCLUSION a
 * proposito: si se agrega una ruta nueva al backend y aqui se olvida, falla fuerte (400 + aviso);
 * con una lista de inclusion el olvido mostraria, en silencio, datos de la sede equivocada.
 *
 * - `inventory/stock-levels` e `inventory/{id}/kardex`: hoy NO filtran por sede.
 */
const SINGLE_SEDE_REPORT_PATHS: readonly RegExp[] = [
  /^inventory\/stock-levels$/,
  /^inventory\/[^/]+\/kardex$/,
];

/**
 * true si el request lleva el alcance de Reportes. De encuestas solo `kpis` (el resto —detalle
 * por id, configuracion— no es un reporte por sede).
 */
export function usesReportsSedeScope(url: string): boolean {
  const path = pathOf(url);
  const reportsIndex = path.indexOf(REPORTS_PATH_PREFIX);
  let applies = false;

  if (reportsIndex >= 0) {
    const relative = path.slice(reportsIndex + REPORTS_PATH_PREFIX.length);
    applies = !SINGLE_SEDE_REPORT_PATHS.some(pattern => pattern.test(relative));
  } else if (path.includes(SURVEYS_PATH_PREFIX)) {
    applies = path.endsWith(SURVEY_KPIS_PATH);
  }

  return applies;
}

function pathOf(url: string): string {
  return url.split('?')[0].replace(/\/+$/, '');
}
